import React, { useState, useEffect, useCallback } from 'react'
import { debounce } from 'lodash'
import PropTypes from 'prop-types'
import { FormControl, Autocomplete, TextField } from '@mui/material'
import { Controller } from 'react-hook-form'
//  import { SORTABLE_IBT_FIELDS } from 'utils/constants'
//  import debounce from 'lodash/debounce'

// this has been modified so all the data will load up front rather than lazily
// once we really need server side filtering this should be looked at again
const AsyncAutocomplete = ({
  name,
  control,
  defaultValue,
  labelKey,
  useGetOptionsLazyQuery,
  freeSolo,
  autoSelect,
  disabled,
  InputProps,
  onDataLoad,
  onChange,
  autocompleteProps,
  formControlProps,
  baseFilter,
  passInputValueToSearchFilter,
  pageSize,
  ...restProps
}) => {
  const [open, setOpen] = useState(false)
  const [inputValue, setInputValue] = useState('')
  const [getOptions, { data, isLoading }] = useGetOptionsLazyQuery()

  const debouncedGetOptions = useCallback(debounce(getOptions, 500), [])

  useEffect(() => {
    if (typeof onDataLoad === 'function') {
      onDataLoad(data)
    }
  }, [data])

  // if its freeSolo mode the options need to be an array of strings
  // so item[labelKey] must be a string
  const options = isLoading || !data ? [] : freeSolo ? data.Items.map(item => item[labelKey]) : data.Items

  useEffect(() => {
    if (!open) return
    if (passInputValueToSearchFilter && inputValue) {
      debouncedGetOptions({
        pageSize,
        filter: JSON.stringify({
          ...baseFilter,
          search: inputValue,
          sort: {
            field: 'displayName',
            ascending: true,
          },
        }),
      })
    } else {
      // only include filter if there is a baseFilter, due to strict BE validation
      debouncedGetOptions({
        pageSize,
        filter: Object.keys(baseFilter).length > 0 ? JSON.stringify({ ...baseFilter }) : undefined,
      })
    }
  }, [open, baseFilter, passInputValueToSearchFilter, inputValue])

  return (
    <FormControl sx={{ width: '100%' }} {...formControlProps}>
      <Controller
        name={name}
        control={control}
        defaultValue={defaultValue}
        render={({ field }) => (
          <Autocomplete
            {...field}
            freeSolo={freeSolo}
            autoSelect={autoSelect}
            options={options}
            // TODO: uncomment below when filter api is ready
            // filterOptions={x => x}
            loading={!data || isLoading}
            open={open}
            disabled={disabled}
            onOpen={() => setOpen(true)}
            onClose={() => setOpen(false)}
            getOptionLabel={option =>
              Object.prototype.hasOwnProperty.call(option, labelKey)
                ? option[labelKey]
                : typeof option === 'string' || option instanceof String
                  ? option
                  : ''
            }
            isOptionEqualToValue={(option, value) => option?.id === value?.id}
            onChange={(_, value) => {
              onChange(value)
              field.onChange(value)
            }}
            {...autocompleteProps}
            onInputChange={(event, value, reason) => {
              autocompleteProps?.onInputChange?.(event, value, reason)
              setInputValue(value)
            }}
            renderInput={params => (
              <TextField {...params} {...restProps} InputProps={{ ...params.InputProps, ...InputProps }} />
            )}
          />
        )}
      />
    </FormControl>
  )
}

AsyncAutocomplete.propTypes = {
  name: PropTypes.string.isRequired,
  variant: PropTypes.string,
  defaultValue: PropTypes.any,
  control: PropTypes.instanceOf(Object).isRequired,
  labelKey: PropTypes.string.isRequired,
  useGetOptionsLazyQuery: PropTypes.func.isRequired,
  freeSolo: PropTypes.bool,
  autoSelect: PropTypes.bool,
  disabled: PropTypes.bool,
  onDataLoad: PropTypes.func,
  onChange: PropTypes.func,
  autocompleteProps: PropTypes.object,
  formControlProps: PropTypes.object,
  InputProps: PropTypes.object,
  baseFilter: PropTypes.object,
  passInputValueToSearchFilter: PropTypes.bool,
  pageSize: PropTypes.number,
}

AsyncAutocomplete.defaultProps = {
  variant: 'standard',
  defaultValue: null,
  freeSolo: false,
  autoSelect: false,
  disabled: false,
  onDataLoad: null,
  onChange: () => {},
  autocompleteProps: {},
  formControlProps: {},
  InputProps: {},
  baseFilter: {},
  passInputValueToSearchFilter: false,
  pageSize: 50,
}

export default AsyncAutocomplete
